import Button from '@/components/button'
import { Formik, Field, Form, ErrorMessage } from 'formik'
import PhoneInput from '@/components/ui/PhoneInput'
import { toFormikValidationSchema } from 'zod-formik-adapter'
import { z } from 'zod'
import statesncities from 'data/statesncities'
import axios from 'axios'
import Select from 'react-select'
import { useState } from 'react'
import { blobToFile } from '@/lib/utils'
import { ImageCropModal } from '@/components/image-crop-modal'

const BusinessInfoSchema = z.object({
  businessName: z
    .string()
    .min(2, 'Business name must be at least 2 characters'),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  email: z.string().email('Invalid email address').or(z.literal('')).optional(),
  phoneNumber: z.string().regex(/^\d{10}$/, 'Phone number must be 10 digits'),
  description: z.string().min(5, 'Description is too short'),
  streetAddress: z.string().nonempty('Street address is required'),
  city: z.string().nonempty('City is required'),
  state: z.string().nonempty('State is required'),
  zip: z.string().regex(/^\d{5}$/, 'Invalid ZIP code'),
  image: z
    .custom<File | null>((val) => val === null || val instanceof File, {
      message: 'Invalid image file',
    })
    .nullable()
    .optional(),
  })

type Option = {
  value: string;
  label: string;
};

interface Image {
  src: string
  type: string
}

interface VendorManualProps {
  onNext?: any
  onBack?: () => void
  onCancel?: () => void
  setFormData: Function
}
const VendorManual = ({ onNext, onBack, onCancel, setFormData }: VendorManualProps) => {
  const initialValues = {
    businessName: '',
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    description: '',
    streetAddress: '',
    city: '',
    state: '',
    zip: '',
    image: null,
    latitude: 0,
    longitude: 0
  }

  const fileName = `cropped-image-${Date.now()}.png`

  const [image, setImage] = useState<string | null>(null)
  const [imageToCrop, setImageToCrop] = useState('')
  const [showCropModal, setShowCropModal] = useState(false)
  const [originalFileName, setOriginalFileName] = useState(fileName)

  const handleFormSubmit = (values: typeof initialValues) => {
    const newVendor = {
      listing_id: null,
      name: values.businessName,
      user_type: 'Producer',
      membership_type: 'Basic',
      email: values.email,
      publicEmail: values.email,
      publicPhone: values.phoneNumber,
      phone: values.phoneNumber,
      latitude: values.latitude,
      longitude: values.longitude,
      city: values.city,
      state: values.state,
      description: values.description,
      address: values.streetAddress,
      zipcode: values.zip,
      enhanced: false,
      pending: false,
      approved: false,
      on_demand: true,
      tagLine: null,
      firstName: values.firstName,
      lastName: values.lastName,
      vendor_avatar: values.image,
      photo: URL.createObjectURL(values.image)
    }

    setFormData((prevFormData: any) => ({
        ...prevFormData,
        vendors: [...prevFormData.vendors, newVendor],
    }))
    
    onCancel && onCancel()

  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const fileUrl = URL.createObjectURL(file);
      setImageToCrop(fileUrl);
      setShowCropModal(true);
      setOriginalFileName(file.name || 'cropped-image.jpg');
    }
  };

  return (
    <div className="px-4 sm:min-w-[626px]">
      <Formik
        initialValues={initialValues}
        validationSchema={toFormikValidationSchema(BusinessInfoSchema)}
        onSubmit={handleFormSubmit}
      >
        {({ setFieldValue, isSubmitting, ...formik }) => {
          const handleCropComplete = (croppedImage: string, croppedBlob: Blob) => {
            const fileImg = blobToFile(croppedBlob, originalFileName);
            setFieldValue('image', fileImg);
            setImage(croppedImage)
            setShowCropModal(false)
          };
          return (
            <Form onSubmit={formik.handleSubmit}  className="flex flex-col gap-8 px-2 pt-4">
              <div className="relative h-full w-full">
                <div className="rounded-none pb-5">
                  <h1 className="mb-2 font-bold">Add profile image</h1>
                  <span className="text-xs">
                    Choose profile photo and move the coordinates to area of the
                    image where you want to focus.
                  </span>
                </div>
                <div className="flex justify-center">
                  {image ? (
                    <div className="h-40 w-40 overflow-hidden rounded-full">
                      <img
                        src={image || '/placeholder.svg'}
                        alt="Profile"
                        className="h-full w-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="flex h-40 w-40 items-center justify-center rounded-full bg-gray-200">
                      <span className="text-gray-500">No Image Selected</span>
                    </div>
                  )}
                </div>

                <div className="mt-3 flex w-full place-content-center gap-4 text-white sm:-bottom-4">
                  <Button
                    onClick={() => {
                      // This button is replaced by the ImageCropModal
                    }}
                    className="rounded-full px-4 py-2 text-sm"
                    title={
                      <span className="text-sm">
                        {image
                          ? 'Change Profile Image'
                          : 'Choose Profile Image'}
                      </span>
                    }
                  />
                  <input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept=".png,.jpg,.jpeg"
                  />
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <label>Business name</label>
                <Field
                  type="text"
                  name="businessName"
                  placeholder="Enter name"
                  className="mt-1 h-14 w-full rounded-lg border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                />
                <ErrorMessage
                  name="businessName"
                  component="div"
                  className="text-sm text-red-500"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="leading-[19.36px] text-[#202020]">
                  Description about business
                </label>
                <Field
                  as="textarea"
                  name="description"
                  placeholder="Add a description of this vendor and/or products."
                  rows={6}
                  className="w-full resize-none rounded-lg border border-[#CBD7DD]  px-4 py-2 placeholder:text-base focus:border-primary focus:ring-primary"
                />
                <ErrorMessage
                  name="description"
                  component="div"
                  className="text-sm text-red-500"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="leading-[19.36px] text-[#202020]">
                  Contact person for this business
                </label>
                <div className="flex gap-6">
                  <div className="flex flex-1 flex-col">
                    <Field
                      type="text"
                      name="firstName"
                      placeholder="First Name"
                      className="h-14 w-full rounded-lg border border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                    />
                    <ErrorMessage
                      name="firstName"
                      component="div"
                      className="text-sm text-red-500"
                    />
                  </div>
                  <div className="flex flex-1 flex-col">
                    <Field
                      type="text"
                      name="lastName"
                      placeholder="Last Name"
                      className="h-14 w-full rounded-lg border border-[#CBD7DD] placeholder:text-base focus:border-primary focus:ring-primary"
                    />
                    <ErrorMessage
                      name="lastName"
                      component="div"
                      className="text-sm text-red-500"
                    />
                  </div>
                </div>
              </div>
              <div>
                <label className="block">Email Address (optional)</label>
                <Field
                  type="email"
                  name="email"
                  placeholder="Enter email"
                  className="mt-1 h-14 w-full rounded-lg border-[#CBD7DD] focus:border-primary focus:ring-primary"
                />
                <ErrorMessage
                  name="email"
                  component="div"
                  className="text-sm text-red-500"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label>Phone Number</label>
                <Field name="phoneNumber">
                  {({ field, form }: any) => (
                    <PhoneInput
                      value={field.value || ''}
                      onChange={(digits) => form.setFieldValue('phoneNumber', digits)}
                    />
                  )}
                </Field>
                <ErrorMessage
                  name="phoneNumber"
                  component="div"
                  className="text-sm text-red-500"
                />
              </div>
              <div className="flex flex-col gap-2">
                <label className="leading-[19.36px] text-[#202020]">
                  Street Address
                </label>
                <Field
                  type="text"
                  name="streetAddress"
                  placeholder="Enter address"
                  className="h-14 w-full rounded-lg border border-[#CBD7DD]  px-4 placeholder:text-base focus:border-primary focus:ring-primary"
                />
                <ErrorMessage
                  name="streetAddress"
                  component="div"
                  className="text-sm text-red-500"
                />
              </div>

              <div className="flex gap-6">
              <div className="flex flex-1 flex-col gap-2">
                  <label className="leading-[19.36px] text-[#202020]">
                    State
                  </label>
                  <Field
                    name="state"
                    render={({ field, form }: any) => (
                      <Select
                        options={statesncities.map((state) => ({ value: state.abbr, label: state.state }))}
                        value={statesncities.map((state) => ({ value: state.abbr, label: state.state })).find((option) => option.value === field.value)}
                        onChange={(option: any) => {
                          form.setFieldValue('state', option ? option.value : '')
                          form.setFieldValue('city', '')
                        }}
                        styles={{
                          control: (provided: any, state: any) => ({
                            ...provided,
                            height: '56px',
                            width: '100%',
                            borderRadius: '0.5rem',
                            borderColor: state.isFocused ? '#2563eb' : '#CBD7DD',
                            padding: '0 1rem',
                            fontSize: '1rem',
                            color: field.value ? '#000000' : '#6b7280',
                            boxShadow: state.isFocused ? '0 0 0 1px #2563eb' : 'none',
                          }),
                          placeholder: (provided: any) => ({
                            ...provided,
                            color: '#6b7280',
                          }),
                        }}
                      />
                    )}
                  />
                  <ErrorMessage
                    name="state"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>
                <div className="flex flex-1 flex-col gap-2">
                  <label className="leading-[19.36px] text-[#202020]">
                    City
                  </label>
                  <Field
                    name="city"
                    render={({ field, form }: any) => {
                      const cities = form.values.state ? (statesncities.find((item) => item.abbr === form.values.state)?.cities.map((city) => ({ value: city, label: city })) || []) : [];
                      return (
                        <Select
                          options={cities}
                          value={cities.find((option) => option.value === field.value)}
                          onChange={(option: any) => {
                            form.setFieldValue('city', option ? option.value : '')
                            if(option && option.value && form.values.state && form.values.streetAddress) {
                              const formattedAddress = `${form.values.streetAddress} ${option.value} ${form.values.state}`
                              axios.get(
                                `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(formattedAddress)}&key=${process.env.NEXT_PUBLIC_GOOGLE_MAP_KEY}`
                              ).then(response => {
                                if(response.status === 200){
                                  const latlng = response?.data?.results[0]?.geometry?.location
                                  const address = response.data.results[0]?.formatted_address
                                  const zipCodeMatch = address?.match(/\b\d{5}\b/);
                                  form.setFieldValue('zip', zipCodeMatch ? zipCodeMatch[0] : null)
                                  form.setFieldValue('latitude', latlng ? Number(latlng?.lat).toFixed(6) : null)
                                  form.setFieldValue('longitude', latlng ? Number(latlng?.lng).toFixed(6) : null)
                                }
                              })
                            }
                          }}
                          isDisabled={!form.values.state}
                          styles={{
                            control: (provided: any, state: any) => ({
                              ...provided,
                              height: '56px',
                              width: '100%',
                              borderRadius: '0.5rem',
                              borderColor: state.isFocused ? '#2563eb' : '#CBD7DD',
                              padding: '0 1rem',
                              fontSize: '1rem',
                              color: field.value ? '#000000' : '#6b7280',
                              boxShadow: state.isFocused ? '0 0 0 1px #2563eb' : 'none',
                            }),
                            placeholder: (provided: any) => ({
                              ...provided,
                              color: '#6b7280',
                            }),
                          }}
                        />
                      );
                    }}
                  />
                  <ErrorMessage
                    name="city"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>
                <div className=" flex flex-1 flex-col gap-2">
                  <label className="leading-[19.36px] text-[#202020]">
                    Zip
                  </label>
                  <Field
                    type="text"
                    name="zip"
                    placeholder="Code"
                    className="h-14 w-full rounded-lg border border-[#CBD7DD]  px-4 placeholder:text-base focus:border-primary focus:ring-primary"
                  />
                  <ErrorMessage
                    name="zip"
                    component="div"
                    className="text-sm text-red-500"
                  />
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-4">
                <Button
                  title="Cancel"
                  onClick={onCancel}
                  bgGreen={false}
                  className="font-normal bg-[#202020] text-white"
                  shadow={false}
                />
                <Button
                  title="Confirm Add"
                  type="submit"
                  disabled={isSubmitting}
                  // onClick={() => {
                  //   console.log(formik.values)
                  //   handleSubmit(formik.values)
                  // }}
                  className="font-normal"
                />
              </div>
              <ImageCropModal
                image={imageToCrop || ''}
                onCropComplete={handleCropComplete}
                open={showCropModal}
                onOpenChange={setShowCropModal}
                aspect={1}
                fileName={originalFileName}
                fileType="image/jpeg"
                quality={0.9}
                maxSizeMB={5}
                maxWidthOrHeight={800}
                outputWidth={400}
                outputHeight={400}
                title="Crop Vendor Image"
                showGrid={true}
                cropShape="round"
              />
            </Form>
          )
        }}
      </Formik>
    </div>
  )
}

export default VendorManual
